import { z } from 'zod/v4';

import { CurrencyRuntype, MonetaryValueBackendRuntype, QuantityRuntype } from '../backendTypes';
import { LifecycleEnum, PartLiteRuntype, StandardPartDTORuntype } from '../part';
import { PcbPanelSpecificationRuntype } from '../pcb';
import { SolutionDTORuntype } from '../solution';
import { SupplierAndStockLocationDTORuntype } from '../supplierAndStockLocation';
import { PriceBreakAvailabilityRuntype } from './availablityBackendTypes';
import {
    AllOriginRuntype,
    CustomOptionOfferResponseRuntype,
    CustomPartPricingModeRuntype,
    ItemClassEnumRunType,
    OfferPaymentTermsRuntype,
    OneTimeCostDTORuntype,
    PriceTypeRuntype,
    StandardPartOfferResponseRuntype,
} from './offerBackendTypes';
import { PackagingRuntype } from './Packaging';

export enum NegotiationOfferIdKind {
    PublicOffTheShelf = 'PublicOffTheShelf',
    PrivateOffTheShelf = 'PrivateOffTheShelf',
    Custom = 'Custom',
    Inventory = 'Inventory',
}

export const NegotiationOfferIdRuntype = z.object({
    kind: z.enum(NegotiationOfferIdKind),
    id: z.uuid(),
});

export type PriceBreakDTO = z.infer<typeof PriceBreakRuntype>;
const PriceBreakRuntype = z.object({
    moq: z.number(),
    unit_price: z.object({
        amount: z.string(),
        currency: CurrencyRuntype,
    }),
    mpq: z.number(),
});

export type OnOrderDTO = z.infer<typeof OnOrderRuntype>;
const OnOrderRuntype = z.object({
    quantity: z.number(),
    date: z.string().nullable(),
});

export interface PriceBreaksDTO extends z.infer<typeof PriceBreaksDTORuntype> {}

export const PriceBreaksDTORuntype = z.object({
    factory_quantity: z.number().nullable(),
    factory_lead_time_days: z.number().nullable(),
    on_order: z.array(OnOrderRuntype),
    stock: z.number().nullable(),
    total_stock: z.number().nullable(),
    price_breaks: z.array(PriceBreakRuntype),
});

export interface PricePointsDTO extends z.infer<typeof PricePointsDTORuntype> {}

const PricePointsDTORuntype = z.object({
    quantity: z.number(),
    unit_price: MonetaryValueBackendRuntype,
    availability: PriceBreakAvailabilityRuntype,
});

export interface CustomPartPriceBreakDTO extends z.infer<typeof CustomPartPriceBreakRuntype> {}

const CustomPartPriceBreakRuntype = z.object({
    minimum_order_quantity: z.number(),
    minimum_packaging_quantity: z.number(),
    price_per_measurement: MonetaryValueBackendRuntype,
    availability: PriceBreakAvailabilityRuntype,
    description: z.string().nullable(),
});

export interface OneTimeCostInputDTO extends z.infer<typeof OneTimeCostInputDTORuntype> {}

const OneTimeCostInputDTORuntype = z.object({
    description: z.undefined().or(z.string()),
    amount: z.string(),
});

const restrictedForResponseSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Customer'),
        data: z.object({
            id: z.uuid(),
            name: z.string(),
            number: z.string().nullable(),
        }),
    }),
    z.object({
        type: z.literal('RfQ'),
        data: z.object({
            id: z.uuid(),
            name: z.string(),
            number: z.string().nullable(),
        }),
    }),
    z.object({
        type: z.literal('Negotiation'),
        data: z.object({
            id: z.uuid(),
            name: z.string(),
        }),
    }),
    z.object({
        type: z.literal('ShipToSite'),
        data: z.object({
            id: z.uuid(),
            name: z.string(),
        }),
    }),
    z.object({
        type: z.literal('Ipn'),
        data: z.object({
            id: z.string(),
        }),
    }),
] as const);

export type RestrictedForResponse = z.infer<typeof restrictedForResponseSchema>;

const restrictedForInputSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Customer'),
        id: z.uuid(),
    }),
    z.object({
        type: z.literal('RfQ'),
        id: z.uuid(),
    }),
    z.object({
        type: z.literal('Negotiation'),
        id: z.uuid(),
    }),
    z.object({
        type: z.literal('ShipToSite'),
        id: z.uuid(),
    }),
    z.object({
        type: z.literal('Ipn'),
        id: z.string(),
    }),
] as const);
export type RestrictedForInput = z.infer<typeof restrictedForInputSchema>;

export enum StandardPartOfferLinkedPartType {
    OffTheShelf = 'OffTheShelf',
    InternalPartNumber = 'InternalPartNumber',
}

export const StandardPartOfferLinkedPartTypeRunType = z.enum(StandardPartOfferLinkedPartType);

export enum StandardPartOfferLinkedLocationType {
    SupplierAndStockLocation = 'SupplierAndStockLocation',
    InventorySite = 'InventorySite',
}

export const StandardPartOfferLinkedLocationTypeRunType = z.enum(StandardPartOfferLinkedLocationType);

const AvailabilityInputDTORuntype = z.object({
    stock: z.number().nullable(),
    factory_lead_time_days: z.number().nullable(),
    factory_quantity: z.number().nullable(),
    on_order: z.array(OnOrderRuntype),
});

export interface PriceBreakInputDTO extends z.infer<typeof PriceBreakInputDTORuntype> {}

const PriceBreakInputDTORuntype = z.object({
    moq: z.number(),
    unit_price: z.number().nullable(),
    mpq: z.number(),
});

export interface StandardPartOfferInputDTO extends z.infer<typeof StandardPartOfferInputDTORuntype> {}

export const StandardPartOfferInputDTORuntype = z.object({
    linked_part: z.object({
        type: StandardPartOfferLinkedPartTypeRunType,
        id: z.string(),
    }),
    supplier_and_stock_location: z.string(),
    supplier_part_number: z.string().nullable(),
    price_type: PriceTypeRuntype,
    restricted_for: z.array(restrictedForInputSchema),
    notes: z.null().or(z.string()),
    valid_until: z.null().or(z.string()),
    one_time_costs: z.array(OneTimeCostInputDTORuntype),
    valid_from: z.null().or(z.string()),
    item_class: ItemClassEnumRunType.nullable(),
    cancellation_window_in_days: z.number().nullable(),
    currency: CurrencyRuntype,
    availability_input: z.null().or(AvailabilityInputDTORuntype),
    price_break_inputs: z.array(PriceBreakInputDTORuntype),
    packaging: z.null().or(PackagingRuntype),
    unit_of_measurement: QuantityRuntype,
    offer_number: z.null().or(z.string()),
    offer_url: z.null().or(z.string()).optional(),
    ncnr: z.null().or(z.boolean()),
});

export interface StandardPartOfferUpdateDTO extends z.infer<typeof StandardPartOfferUpdateDTORuntype> {}

export const StandardPartOfferUpdateDTORuntype = StandardPartOfferInputDTORuntype.pick({
    supplier_part_number: true,
    supplier_and_stock_location: true,
    price_type: true,
    restricted_for: true,
    notes: true,
    valid_until: true,
    valid_from: true,
    item_class: true,
    cancellation_window_in_days: true,
    one_time_costs: true,
    currency: true,
    availability_input: true,
    price_break_inputs: true,
    packaging: true,
    unit_of_measurement: true,
    offer_number: true,
    ncnr: true,
});

export enum ValidFor {
    EveryCustomer = 'every-customer',
    ThisCustomer = 'this-customer',
    ThisRfQ = 'this-rfq',
    ThisNegotiation = 'this-negotiation',
}

export enum CustomOptionOfferLinkedPartType {
    CustomPart = 'CustomPart',
    CustomComponent = 'CustomComponent',
}

export const CustomOptionOfferLinkedPartTypeRunType = z.enum(CustomOptionOfferLinkedPartType);

export type CustomLinkedPartDTO = z.infer<typeof CustomLinkedPartDTORuntype>;
export const CustomLinkedPartDTORuntype = z.union([
    z.object({
        type: z.literal(CustomOptionOfferLinkedPartType.CustomPart),
        id: z.string(),
    }),
    z.object({
        type: z.literal(CustomOptionOfferLinkedPartType.CustomComponent),
        id: z.string(),
    }),
]);

/** @deprecated Use the offer response types in offerBackendTypes.ts. */
const BaseCustomPartOfferDTORuntype = z.object({
    id: z.string(),
    origin: AllOriginRuntype,
    creation_date: z.string(),
    has_order_files: z.boolean(),
    unit_of_measurement: QuantityRuntype,
    restricted_for: z.array(restrictedForResponseSchema),
    valid_from: z.null().or(z.string()),
    valid_until: z.null().or(z.string()),
    one_time_costs: z.array(OneTimeCostDTORuntype),
    notes: z.null().or(z.string()),
    item_class: ItemClassEnumRunType.nullable(),
    offer_number: z.null().or(z.string()),
    offer_url: z.null().or(z.string()),
    price_type: PriceTypeRuntype.nullable(),
    specification: PcbPanelSpecificationRuntype.nullable(),
    sourcing_batch_size: z.number().nullable(),
    total_offered_quantity: z.number().nullable(),
    price_breaks: z.array(CustomPartPriceBreakRuntype),
    payment_terms: OfferPaymentTermsRuntype.nullable().optional(),
    supplier_part_number: z.string().nullable(),
    cancellation_window_in_days: z.number().nullable(),
    attachment: z.string().nullable().optional(),
    pricing_mode: CustomPartPricingModeRuntype.nullable(),
});

/** @deprecated Use the offer response types in offerBackendTypes.ts. */
export type CustomPartMarketOfferDTO = z.infer<typeof CustomPartMarketOfferDTORuntype>;
/** @deprecated Use the offer response types in offerBackendTypes.ts. */
export const CustomPartMarketOfferDTORuntype = BaseCustomPartOfferDTORuntype.extend({
    linked_part: z.object({
        type: z.literal(CustomOptionOfferLinkedPartType.CustomPart),
        id: z.string(),
    }),
    linked_location: SupplierAndStockLocationDTORuntype.extend({
        type: z.literal(StandardPartOfferLinkedLocationType.SupplierAndStockLocation),
    }),
});

const QuoteRequestOffersCommonFields = {
    supplier_part_number: z.string().optional().nullable().default(''),
    supplier_and_stock_location: z.string(),
    restricted_for: z.array(restrictedForInputSchema),
    notes: z
        .string()
        .nullable()
        .optional()
        .transform((val) => val || ''),
    creation_date: z.string(),
    valid_until: z.string().nullable(),
    valid_from: z.string().nullable(),
    item_class: ItemClassEnumRunType.nullable(),
    cancellation_window_in_days: z.number().nullable(),
    one_time_costs: z.array(OneTimeCostDTORuntype),
    offer_number: z.string().nullable(),
    packaging: PackagingRuntype.nullable().optional(),
    unit_of_measurement: QuantityRuntype,
    offer_url: z.string().nullable(),
    attachment: z.unknown().nullable(),
    ncnr: z.boolean().nullable().optional(),
    payment_terms: OfferPaymentTermsRuntype.nullable().optional(),
    part: PartLiteRuntype,
};
export type QuoteRequestOfferDTO = z.infer<typeof QuoteRequestOfferDTORuntype>;
export const QuoteRequestOfferDTORuntype = z.union([
    CustomPartMarketOfferDTORuntype.extend({
        kind: z.literal('Custom'),
        id: z.string(),
        part: PartLiteRuntype,
    }),
    z.object({
        ...QuoteRequestOffersCommonFields,
        kind: z.literal('OffTheShelf'),
        id: z.object({
            offer: z.string(),
        }),

        available_prices: PriceBreaksDTORuntype.optional(),
    }),
]);

export interface CustomPricePointInputDTO extends z.infer<typeof CustomPricePointInputDTORuntype> {}

const CustomPricePointInputDTORuntype = z.object({
    quantity: z.number(),
    amount: z.string(),
    lead_time_days: z.undefined().or(z.number()),
});

export interface CustomPartPriceBreakInputDTO extends z.infer<typeof CustomPartPriceBreakInputDTORuntype> {}

const CustomPartPriceBreakInputDTORuntype = z.object({
    minimum_order_quantity: z.number(),
    minimum_packaging_quantity: z.number(),
    amount: z.number(),
    lead_time_days: z.undefined().or(z.number()),
    description: z.undefined().or(z.string()),
});

export interface CustomPartOfferInputDTO extends z.infer<typeof CustomPartOfferInputDTORuntype> {}

export const CustomPartOfferInputDTORuntype = z.object({
    linked_part: CustomLinkedPartDTORuntype,
    supplier_and_stock_location: z.string(),
    unit_of_measurement: QuantityRuntype,
    currency: CurrencyRuntype,
    restricted_for: z.array(restrictedForInputSchema),
    valid_from: z.undefined().or(z.string()),
    valid_until: z.undefined().or(z.string()),
    one_time_costs: z.array(OneTimeCostInputDTORuntype),
    price_type: z.undefined().or(PriceTypeRuntype),
    offer_number: z.undefined().or(z.string()),
    notes: z.undefined().or(z.string()),
    sourcing_scenario_id: z.undefined().or(z.string()),
    assembly_id: z.undefined().or(z.string()),
    sourcing_batch_size: z.undefined().or(z.number()).or(z.null()),
    total_offered_quantity: z.number().optional(),
    price_breaks: z.array(CustomPartPriceBreakInputDTORuntype),
    supplier_part_number: z.string().optional(),
    attachment: z.string().optional(),
    pricing_mode: CustomPartPricingModeRuntype.optional(),
    lifecycle: z.enum(LifecycleEnum).optional().nullable(),
});

export interface CustomPartOfferUpdateDTO extends z.infer<typeof CustomPartOfferUpdateDTORuntype> {}

export const CustomPartOfferUpdateDTORuntype = z.object({
    linked_part: CustomLinkedPartDTORuntype,
    supplier_and_stock_location: z.string(),
    unit_of_measurement: QuantityRuntype,
    currency: CurrencyRuntype,
    restricted_for: z.array(restrictedForInputSchema),
    valid_until: z.null().or(z.string()),
    valid_from: z.null().or(z.string()),
    one_time_costs: z.array(OneTimeCostInputDTORuntype),
    notes: z.null().or(z.string()),
    price_type: z.undefined().or(PriceTypeRuntype),
    sourcing_batch_size: z.undefined().or(z.number()).or(z.null()),
    total_offered_quantity: z.number().optional(),
    price_breaks: z.array(CustomPartPriceBreakInputDTORuntype),
    supplier_part_number: z.string().optional(),
    pricing_mode: CustomPartPricingModeRuntype.nullable().optional(),
    lifecycle: z.enum(LifecycleEnum).optional().nullable(),
});

export interface BulkCustomPartOfferRequestDTO extends z.infer<typeof BulkCustomPartOfferRequestDTORuntype> {}

const QuoteEventMetadataRuntype = z.union([
    z.object({ type: z.literal('Excel') }),
    z.object({ type: z.literal('Pdf') }),
    z.object({ type: z.literal('SupplierPortal') }),
]);

export const BulkCustomPartOfferRequestDTORuntype = z.object({
    custom_part_offers: z.array(CustomPartOfferInputDTORuntype),
    rfq_id: z.uuid().optional(),
    supplier_and_stock_location_id: z.uuid(),
    event_metadata: QuoteEventMetadataRuntype,
});

const BoundingRegion = z.object({
    pageNumber: z.number(),
    polygon: z.array(z.number()),
});

const ObjectTypeCurrencyRuntype = z.object({
    type: z.literal('currency'),
    valueCurrency: z
        .object({
            amount: z.number(),
        })
        .optional(),
    boundingRegions: z.array(BoundingRegion),
    confidence: z.number(),
    content: z.string(),
});

const ObjectTypeNumberRuntype = z.object({
    type: z.literal('number'),
    valueNumber: z.number(),
    boundingRegions: z.array(BoundingRegion),
    confidence: z.number(),
    content: z.string(),
});

const ObjectTypeStringRuntype = z.object({
    type: z.literal('string'),
    valueString: z.string(),
    boundingRegions: z.array(BoundingRegion),
    confidence: z.number(),
    content: z.string(),
});

const ObjectTypeDateRuntype = z.object({
    type: z.literal('date'),
    valueDate: z.string().optional(),
    boundingRegions: z.array(BoundingRegion),
    confidence: z.number(),
    content: z.string(),
});

export interface ObjectTypeObject extends z.infer<typeof ObjectTypeObjectRuntype> {}

const ObjectTypeObjectRuntype = z.object({
    type: z.literal('object'),
    valueObject: z
        .object({
            UnitPrice: ObjectTypeCurrencyRuntype.optional(),
            ProductCode: ObjectTypeStringRuntype.optional(),
            Unit: ObjectTypeStringRuntype.optional(),
            Quantity: ObjectTypeNumberRuntype.optional(),
            Amount: ObjectTypeCurrencyRuntype.optional(),
        })
        .optional(), // very annoyingly so there's been occasions where Azure just skips this
    boundingRegions: z.array(BoundingRegion),
    confidence: z.number(),
    content: z.string(),
});

const ObjectTypeArrayRuntype = z.object({
    type: z.literal('array'),
    valueArray: z.array(ObjectTypeObjectRuntype),
});

// Rust's PartUpdateStatus, serde externally tagged.
const PartUpdateStatusRuntype = z.union([
    z.literal('Ok'),
    z.object({ Error: z.unknown().nullable() }),
    z.object({ RateLimited: z.string().nullable() }),
]);

// Rust's OfferUpdateStatus, serde externally tagged.
export const PartOfferUpdateStatusRuntype = z.union([
    z.literal('InactiveAPI'),
    z.object({ InProgress: z.object({ progress_id: z.string() }) }),
    z.object({ RateLimited: z.object({ seconds_to_expiry: z.number() }) }),
    z.object({ RecentlyUpdated: z.object({ last_part_status: PartUpdateStatusRuntype }) }),
]);

export interface AzureExtractionResult extends z.infer<typeof AzureExtractionResultRuntype> {}

export const AzureExtractionResultRuntype = z.object({
    analyzeResult: z.object({
        pages: z.array(
            z.object({
                pageNumber: z.number(),
                width: z.number(),
                height: z.number(),
                lines: z.array(
                    z.object({
                        polygon: z.array(z.number()),
                        content: z.string(),
                    }),
                ),
                words: z.array(
                    z.object({
                        polygon: z.array(z.number()),
                        content: z.string(),
                    }),
                ),
            }),
        ),
        tables: z.array(
            z.object({
                cells: z.array(
                    z.object({
                        kind: z.literal('columnHeader').optional(),
                        columnIndex: z.number(),
                        rowIndex: z.number(),
                        columnSpan: z.number().optional(),
                        content: z.string(),
                        boundingRegions: z.array(BoundingRegion),
                    }),
                ),
            }),
        ),
        paragraphs: z
            .array(
                z.object({
                    content: z.string(),
                    boundingRegions: z.array(BoundingRegion),
                }),
            )
            .nullable(),
        keyValuePairs: z.array(
            z.object({
                key: z.object({
                    content: z.string(),
                    boundingRegions: z.array(BoundingRegion),
                }),
                value: z
                    .object({
                        content: z.string(),
                        boundingRegions: z.array(BoundingRegion),
                    })
                    .optional(),
                confidence: z.number(),
            }),
        ),
        documents: z.array(
            z.object({
                docType: z.literal('invoice'),
                boundingRegions: z.array(BoundingRegion),
                fields: z.object({
                    AmountDue: z
                        .object({
                            content: z.string(),
                        })
                        .optional(),
                    DueDate: ObjectTypeDateRuntype.optional(),
                    InvoiceDate: ObjectTypeDateRuntype.optional(),
                    InvoiceId: ObjectTypeStringRuntype.optional(),
                    InvoiceTotal: z
                        .object({
                            content: z.string(),
                        })
                        .optional(),
                    Items: ObjectTypeArrayRuntype.optional(),
                    ServiceEndDate: ObjectTypeDateRuntype.optional(),
                    SubTotal: z
                        .object({
                            content: z.string(),
                        })
                        .optional(),
                }),
            }),
        ),
    }),
});

export type PdfAnalyzeResponse = z.infer<typeof PdfAnalyzeResponseRuntype>;
export const PdfAnalyzeResponseRuntype = z.intersection(
    AzureExtractionResultRuntype.shape.analyzeResult,
    z.object({
        attachment: z.string().optional(),
    }),
);

const QuoteImportEventTypeEnumRuntype = z.union([z.literal('Pdf'), z.literal('Excel'), z.literal('SupplierPortal')]);

export const QuoteImportEventRuntype = z.object({
    type: QuoteImportEventTypeEnumRuntype,
});

export interface StandardPartOfferBulkInputDTO extends z.infer<typeof StandardPartOfferBulkInputDTORuntype> {}

export const StandardPartOfferBulkInputDTORuntype = z.object({
    requested_part: StandardPartDTORuntype,
    linked_part: StandardPartDTORuntype,
    supplier_part_number: z.string().optional(),
    supplier_and_stock_location: z.string(),
    price_type: PriceTypeRuntype,
    restricted_for: z.array(restrictedForInputSchema),
    notes: z.string().nullable(),
    valid_until: z.string().nullable(),
    valid_from: z.string().nullable().optional(),
    currency: CurrencyRuntype,
    availability_input: AvailabilityInputDTORuntype.nullable(),
    price_break_inputs: z.array(PriceBreakInputDTORuntype),
    packaging: PackagingRuntype.nullable(),
    unit_of_measurement: QuantityRuntype,
    offer_number: z.string().nullable(),
    attachment: z.string().nullable(),
    ncnr: z.boolean().nullable(),
});

export const ValidForRuntype = z.union([z.literal('Customer'), z.literal('RfQ'), z.literal('Negotiation')]);

export type OfferRef = z.infer<typeof OfferRefRuntype>;
export const OfferRefRuntype = z.union([
    z.object({
        kind: z.literal('StandardPartOffer'),
        id: z.string(),
    }),
    z.object({
        kind: z.literal('CustomOptionOffer'),
        id: z.string(),
    }),
]);

export interface NegotiationsStandardPartOfferBulkInputDTO extends z.infer<
    typeof NegotiationsStandardPartOfferBulkInputDTORuntype
> {}
export const NegotiationsStandardPartOfferBulkInputDTORuntype = z.object({
    quote_request_line_item_id: z.number().nullable(),
    linked_part: StandardPartDTORuntype,
    supplier_part_number: z.string().nullable(),
    supplier_and_stock_location: z.string(),
    price_type: PriceTypeRuntype,
    notes: z.string().nullable(),
    valid_until: z.string().nullable(),
    valid_from: z.string().nullable(),
    item_class: ItemClassEnumRunType.nullable(),
    cancellation_window_in_days: z.number().nullable(),
    bid: z.boolean(),
    currency: CurrencyRuntype,
    one_time_costs: z.array(OneTimeCostInputDTORuntype),
    availability_input: AvailabilityInputDTORuntype.nullable(),
    price_break_inputs: z.array(PriceBreakInputDTORuntype),
    packaging: PackagingRuntype.nullable(),
    unit_of_measurement: QuantityRuntype,
    offer_number: z.string().nullable(),
    attachment: z.string().nullable(),
    ncnr: z.boolean().nullable(),
    payment_terms: OfferPaymentTermsRuntype.nullable(),
    // Absolute restriction terms; when present, even empty ("no restriction"), they override `valid_for`.
    restricted_for: z.array(restrictedForInputSchema).optional(),
    /** @deprecated Use `restricted_for`; still sent for the scopes the frontend can't resolve itself. */
    valid_for: ValidForRuntype.nullable(),
    approval_status: z.enum(['Pending', 'Approved', 'Rejected']).optional(),
    approval_status_notes: z.string().nullable().optional(),
});

export interface NegotiationsCustomPartOfferBulkInputDTO extends z.infer<
    typeof NegotiationsCustomPartOfferBulkInputDTORuntype
> {}
export const NegotiationsCustomPartOfferBulkInputDTORuntype = z.object({
    linked_part: CustomLinkedPartDTORuntype,
    supplier_and_stock_location: z.string(),
    unit_of_measurement: QuantityRuntype,
    currency: CurrencyRuntype,
    // NOTE: silently ignored — the backend has no restriction support for
    // custom part offers in the quote request import (see SOU-4152).
    restricted_for: z.array(restrictedForInputSchema),
    valid_until: z.string().nullable(),
    valid_from: z.string().nullable(),
    item_class: ItemClassEnumRunType.nullable(),
    cancellation_window_in_days: z.number().nullable(),
    one_time_costs: z.array(OneTimeCostInputDTORuntype),
    price_type: PriceTypeRuntype.nullable(),
    notes: z.string().nullable(),
    offer_number: z.string().nullable(),
    payment_terms: OfferPaymentTermsRuntype.nullable(),
    offer_url: z.string().nullable(),
    sourcing_scenario_id: z.string().nullable(),
    sourcing_batch_size: z.number().nullable(),
    quote_request_line_item_id: z.number().nullable(),
    total_offered_quantity: z.number().nullable(),
    price_breaks: z.array(CustomPartPriceBreakInputDTORuntype),
    supplier_part_number: z.string().nullable(),
    bid: z.boolean(),
    attachment: z.string().nullable(),
    approval_status: z.enum(['Pending', 'Approved', 'Rejected']).optional(),
    approval_status_notes: z.string().nullable().optional(),
});

export type QuoteImportOfferInputDTO = z.infer<typeof QuoteImportOfferInputDtoRuntype>;
const QuoteImportOfferInputDtoRuntype = z
    .object({
        type: z.literal('off_the_shelf'),
    })
    .extend(NegotiationsStandardPartOfferBulkInputDTORuntype.shape);

export type QuoteImportCustomPartOfferInputDTO = z.infer<typeof QuoteImportCustomPartOfferInputDTORuntype>;
const QuoteImportCustomPartOfferInputDTORuntype = z
    .object({
        type: z.literal('custom'),
    })
    .extend(NegotiationsCustomPartOfferBulkInputDTORuntype.shape);

export type QuoteRequestOfferImportDTO = z.infer<typeof QuoteRequestOfferImportDTORuntype>;
export const QuoteRequestOfferImportDTORuntype = z.union([
    QuoteImportOfferInputDtoRuntype,
    QuoteImportCustomPartOfferInputDTORuntype,
]);

export interface StandardPartOfferWithSolutionsDTO extends z.infer<typeof StandardPartOfferWithSolutionsDTORuntype> {}
export const StandardPartOfferWithSolutionsDTORuntype = z.object({
    offer: StandardPartOfferResponseRuntype,
    solutions: z.array(
        z.object({
            quantity: z.number(),
            fastest: SolutionDTORuntype.nullable(),
            best_price: SolutionDTORuntype.nullable(),
        }),
    ),
});

export type CustomPartOfferWithSolutionsDTO = z.infer<typeof CustomPartOfferWithSolutionsDTORuntype>;
export const CustomPartOfferWithSolutionsDTORuntype = z.object({
    offer: CustomOptionOfferResponseRuntype,
    solutions: z.array(
        z.object({
            unit_of_measurement: QuantityRuntype,
            fastest: SolutionDTORuntype.nullable(),
            best_price: SolutionDTORuntype.nullable(),
        }),
    ),
});

// Keep this in sync with the backend type: EXPECTED_UNITS
export const ImportQuantityUnitRuntype = z.enum([
    'Piece',
    'Pair',
    'Kilogram',
    'Gram',
    'Milligram',
    'Meter',
    'Centimeter',
    'Millimeter',
    'Liter',
    'Centiliter',
    'Milliliter',
    'Ounce',
    'Gallon',
    'Pint',
    'Foot',
    'Inch',
    'Pound',
]);

export const ImportQuantityRuntype = z.object({
    unit: ImportQuantityUnitRuntype,
    quantity: z.string(),
});

export interface ImportOffersRequestBody extends z.infer<typeof ImportOffersRequestBodyRuntype> {}

export const ImportOffersRequestBodyRuntype = z.object({
    part: z.union([
        z.object({
            internal_part_number: z.string(),
        }),
        z.object({
            internal_part_number: z.string().optional(),
            manufacturer_part_number: z.string(),
            manufacturer: z.string(),
        }),
    ]),
    customer: z
        .object({
            name: z.string().optional(),
            number: z.string(),
        })
        .optional(),
    availability: z
        .object({
            available_stock: z.number(),
            total_stock: z.number().nullish(),
            lead_time: z.number().optional(),
        })
        .optional(),
    notes: z.string().optional(),
    packaging: PackagingRuntype.nullish(),
    price_type: PriceTypeRuntype.optional(),
    unit_of_measurement: ImportQuantityRuntype.optional(),
    valid_until: z.string().optional(),
    valid_from: z.string().optional(),
    supplier: z.union([
        z.object({
            type: z.literal('Internal'),
        }),
        z.object({
            type: z.literal('External'),
            supplier_number: z.string(),
            supplier: z.string().optional(),
        }),
    ]),
    prices: z.array(
        z.object({
            moq: z.number().optional(),
            mpq: z.number().optional(),
            unit_price: MonetaryValueBackendRuntype,
        }),
    ),
    // Existing offer import context id, mutually exclusive with the legacy `import_context` name.
    import_context_id: z.string().uuid().optional(),
});
